import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProductVideoLink from '../components/ProductVideoLink.jsx';
import { api } from '../services/api/client.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { usePlanCatalog } from '../hooks/usePlanCatalog.js';
import { TRIAL_DAYS, startCheckout } from '../lib/plans.js';
import { planHasTrial } from '../lib/planTrial.js';
import { usePendingOAuth } from '../contexts/PendingOAuthContext.jsx';
import { isChatGptOrigin } from '../lib/signupSource.js';
import { buildPlanTracks, gridColsClass } from '../lib/planTracks.js';
import { clearPlanIntent, getPlanIntent, resolvePreselectedPlan } from '../lib/planIntent.js';
import { resolveScheduledDowngrade, resolveSwitchError } from '../lib/planSwitchTrial.js';
import { paymentIssueLinks } from '../lib/paymentIssue.js';
import { resolveManagedPaymentsNewSubscriptions } from '../lib/managedPayments.js';
import { track } from '../lib/funnel.js';
import { resolveSocialAddonState, resolveSocialAddonPricing, resolveIncludedForPlanCard, shouldOfferAccountsSheet } from '../lib/socialAddon.js';
import SocialAddonProvenanceLine from '../components/billing/SocialAddonProvenanceLine.jsx';
import ConnectedAccountsSheet from '../components/billing/ConnectedAccountsSheet.jsx';
import PlanSwitchReviewDialog from '../components/billing/PlanSwitchReviewDialog.jsx';
import { buildPlanSwitchReview, planSwitchReviewMatches } from '../lib/schedulerSwitchReview.js';
import { isRecognizedBillingState } from '../lib/billingState.js';
import { wallContextKeys } from '../lib/freePreview.js';
import '../styles/trial-card-capture.css';
import '../styles/plan-select-screen.css';

// TASK-331: superseded by accessState where present (see getBillingState
// below) — kept only as the fail-soft fallback for an old backend.
const LIVE_SUB_STATUSES = new Set(['active', 'trialing', 'checkout_completed', 'past_due']);
// This screen only ever renders when RequireActiveBilling's cardGateDecision
// resolved 'gate', i.e. accessState === 'new' — so hasLiveSubscription is
// normally false by construction. It stays accessState-aware anyway so a
// stale/racing fetch mid-render can't wrongly claim a live subscription.
// TASK-331 phase 2 (Subscription Schedules): 'downgrade_scheduled' is still a
// live subscription (on its current plan) same as 'active'/'cancel_scheduled'
// — kept in this set for the same defensive reason, even though it can never
// occur alongside accessState 'new' in practice.
const ACCESS_STATES_WITH_LIVE_SUBSCRIPTION = new Set(['trialing', 'active', 'grace', 'cancel_scheduled', 'downgrade_scheduled']);
const VIDEO_FEATURE = /video|cinematic|slideshow/i;

function CheckIcon() {
  return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>;
}

function LockIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>;
}

function ArrowIcon() {
  return <svg className="arr" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>;
}

function getBillingState(ent) {
  const data = ent?.data || ent || {};
  const billingStatus = data.billingStatus || data.subscriptionStatus || null;
  const accessState = data.accessState || null;
  const hasLiveSubscription = accessState
    ? ACCESS_STATES_WITH_LIVE_SUBSCRIPTION.has(accessState)
    : LIVE_SUB_STATUSES.has(billingStatus) || !!data.paymentIssue;
  return {
    billingStatus,
    accessState,
    // TASK-331: hasSubscriptionHistory (present once the backend ships it) is
    // the "was ever a customer" bit — true for a lapsed/canceled returning
    // customer even though hasLiveSubscription above is false for them.
    hasSubscriptionHistory: data.hasSubscriptionHistory === true,
    hasLiveSubscription,
    currentPlanId: data.plan || data.planKey || null,
    cancelAtPeriodEnd: data.cancelAtPeriodEnd,
    scheduledPlan: data.scheduledPlan || null,
    scheduledPlanEffectiveAt: data.scheduledPlanEffectiveAt || null,
    // TASK-329: the Scheduler add-on overage field group (slice B) — the
    // ONE source this screen reads for connected/unitAmount/currency (the
    // included count per CANDIDATE card comes from usePlanCatalog instead,
    // since this org has no current plan yet to read an included count
    // from). Null when there's no org context or the best-effort backend
    // lookup failed — every consumer below already treats null as "don't
    // show the provenance line", same as everywhere else this field group
    // is read.
    socialAccounts: data.socialAccounts || null,
    // GH-912: the gateway's mirror of the stripe-worker's Managed Payments
    // flag for NEW subscriptions (absent on an older backend = false).
    managedPaymentsNewSubscriptions: data.managedPaymentsNewSubscriptions === true,
  };
}

export default function PlanSelectScreen({ onComplete, previewContext = null, onBack = null, returnTo: returnToOverride = null, onBeforeCheckout = null }) {
  const { t, i18n } = useTranslation();
  const { logout, user } = useAuth();
  const pendingOAuth = usePendingOAuth();
  const chatGptOrigin = isChatGptOrigin(pendingOAuth.pending, user);
  const { getNewPlanDisplay, getPostingPlanDisplay, postingPlanActive, managedPaymentsNewSubscriptions: catalogManagedPayments } = usePlanCatalog();
  const [selectedPlanKey, setSelectedPlanKey] = useState('growth');
  // TASK-313: ?plan=<key> intent captured at boot (main.jsx) into localStorage
  // — read once here, same as isChatGptOrigin reads its own persisted source.
  const [planIntentKey] = useState(() => getPlanIntent());
  const appliedIntentTrackedRef = useRef(false);
  const [connectedSocials, setConnectedSocials] = useState([]);
  const [billing, setBilling] = useState({ state: 'loading', billingStatus: null, hasSubscriptionHistory: false, hasLiveSubscription: false, socialAccounts: null });
  // TASK-329 (owner decision, item 2): the connected-accounts sheet, shared
  // across every AI card — the underlying accounts list is the SAME
  // regardless of which candidate card opened it; only the header's
  // provenance text (and the anchor to reposition the desktop popover near)
  // changes per card. Round 2 fix: stores only WHICH plan's sheet is open,
  // not a frozen pricing snapshot — the `pricing` prop is recomputed live
  // from socialAddonPricingFor on every render, so a disconnect inside the
  // sheet (refetches `billing` via onDisconnected) updates the sheet's own
  // header instead of leaving it on the number that was true when it
  // opened.
  const [addonSheet, setAddonSheet] = useState({ open: false, planId: null, planName: null });
  // MINOR 4 (round 2 fix): a real, persistent ref — not a fresh
  // `{ current: ... }` object literal recreated every render.
  const addonSheetAnchorRef = useRef(null);
  const [mode, setMode] = useState('select');
  const [error, setError] = useState(null);
  // TASK-331 SPA fixes (MINOR 2/3, round 6): a resolved switch error (see
  // resolveSwitchError) or the requiresAction-without-a-hosted-invoice-URL
  // fallback is always terminal — checkout.tryAgain is never appended to
  // either, since both already state the actual next step — and separately
  // tells render time whether to show the "Open billing settings" link. null
  // for an unresolved/generic error, which keeps its pre-existing
  // retry-suffix behaviour.
  const [errorMeta, setErrorMeta] = useState(null);
  // TASK-298: "See all plans" toggle — collapsed by default (component-local,
  // no persistence, no URL param). Agency stays reachable while collapsed
  // through "Compare full details" below, which always lists every plan.
  const [showAllPlans, setShowAllPlans] = useState(false);
  const busyRef = useRef(false);
  const [switchReview, setSwitchReview] = useState(null);
  const [switchReviewLoading, setSwitchReviewLoading] = useState(false);
  const [switchReviewError, setSwitchReviewError] = useState(false);
  const [switchReviewChanged, setSwitchReviewChanged] = useState(false);
  const reviewRequestRef = useRef(0);
  const reviewLifecycleRef = useRef(0);
  const reviewGenerationRef = useRef(0);
  const activeReviewRef = useRef(null);
  const operationRef = useRef(null);
  const renderContextRef = useRef(null);

  const tx = (key, fallback) => (key ? t(`settings.${key}`, { defaultValue: fallback }) : fallback);

  // TASK-298: single "all plans in display order" source (design brief
  // lib/planTracks.js) — the Scheduler (Track 1) band plus the three-tier AI
  // grid (Track 2) and the Agency "See all plans" extra. 'posting' only ever
  // appears in publishTrack, never in aiTrack/extraTrack.
  const { publishTrack, aiTrack, extraTrack } = buildPlanTracks({
    getNewPlanDisplay,
    getPostingPlanDisplay,
    postingPlanActive,
    tx,
    t,
    connectedSocials,
  });

  // Video generation is product-wide OFF. Filter the RAW (untranslated)
  // source feature text before translating — matches the pre-TASK-298
  // behaviour exactly, so a locale's translated string can never accidentally
  // reintroduce a removed line (regex only ever matches the English source).
  const translateAiCard = (card) => ({
    id: card.id,
    priceCents: card.priceCents,
    featured: card.translated.featured,
    name: card.translated.name,
    description: card.translated.description,
    price: card.translated.price,
    unit: card.translated.unit,
    features: (card.feats || [])
      .map((feature, index) => ({ feature, key: card.keys?.feats?.[index] }))
      .filter(({ feature }) => !VIDEO_FEATURE.test(feature))
      .map(({ feature, key }) => tx(key, feature)),
  });
  const regularPlans = aiTrack.map(translateAiCard)
    .map((plan) => ({ ...plan, features: [t('postingPlan.higherTierContrast'), ...plan.features] }));
  const extraPlans = extraTrack.map(translateAiCard)
    .map((plan) => ({ ...plan, features: [t('postingPlan.higherTierContrast'), ...plan.features] }));
  // The visible AI grid — respects the collapse toggle.
  const aiCards = showAllPlans ? [...regularPlans, ...extraPlans] : regularPlans;

  // TASK-329 (owner decision, item 1): the Scheduler add-on price-provenance
  // line under each AI card — "N connected · <Plan> includes K -> +(N-K) x
  // €3.99 = €X/mo" for the CANDIDATE plan the card represents (not
  // necessarily any plan the org is on yet, since this screen only renders
  // pre-subscription). `connected`/`unitAmount`/`currency` come from the ONE
  // shared source (billing.socialAccounts, GET /api/billing/entitlements'
  // slice-B field group); `included` is resolved per-card from the plan
  // catalog (usePlanCatalog), since entitlements only ever resolves the
  // included count for the org's OWN current plan.
  const socialAddonState = resolveSocialAddonState(billing.socialAccounts);
  const socialAddonPricingFor = (planId, planName) => {
    // Round 3 fix (minor 6): the Scheduler ('posting') band already shows
    // its own €3.99 x N pricing — the add-on sibling control below (which
    // reads this for `selected?.id`, and `selected` DOES include the
    // posting entry once that band is picked) must never layer a SECOND,
    // unrelated price on top of it, regardless of whatever the admin
    // catalog happens to set for a 'posting' row's social_accounts_total.
    if (planId === 'posting' || socialAddonState.mode === 'hidden') return null;
    return resolveSocialAddonPricing({
      mode: socialAddonState.mode,
      connected: socialAddonState.connected,
      included: resolveIncludedForPlanCard(planId, getNewPlanDisplay(planId)?.limits),
      unitAmount: socialAddonState.unitAmount,
      currency: socialAddonState.currency,
      planName,
      locale: i18n.language || 'en',
      t,
    });
  };

  // TASK-331 phase-1-fixes round 3 (fix #4): the defensive branch described
  // below the stale-comment fix — this screen must not promise "start your
  // free trial" to an org whose hasSubscriptionHistory is true, even though
  // that combination should no longer occur for accessState 'new'. Defaults
  // to true (trial-eligible) while billing.state is 'loading', matching this
  // screen's pre-fix behaviour for the brief instant before the entitlements
  // fetch resolves. Hoisted above compareList (Founder request 2026-09-09)
  // since the trial-chip compare row below needs it too.
  const offerTrial = !billing.hasSubscriptionHistory;

  const postingPlanEntry = publishTrack ? {
    id: 'posting',
    name: t('postingPlan.name'),
    price: publishTrack.line.unitFormatted,
    unit: t('postingPlan.unit'),
  } : null;
  // "Compare full details" always lists every plan — Scheduler + all four AI
  // tiers, Agency included even while the visible grid is collapsed.
  // Founder request (2026-09-09): compare table leads with the same trial
  // fact as the chips above it, reusing the identical chip copy (no new
  // keys) — "7-day free trial" / "No free trial" — instead of the visible
  // grid's own features arrays (regularPlans/extraPlans), which stay
  // untouched since they're also sliced to 4 for the AI cards above and
  // must not gain an extra line there.
  const compareTrialLine = (planId) => (offerTrial
    ? (planHasTrial(planId) ? t('checkout.freeTrial', { days: TRIAL_DAYS }) : t('checkout.noTrialChip'))
    : null);
  const compareList = [
    ...(publishTrack ? [{
      id: 'posting',
      name: t('postingPlan.name'),
      features: [
        ...(compareTrialLine('posting') ? [compareTrialLine('posting')] : []),
        ...[1, 2, 3].map((index) => t(`postingPlan.included${index}`)), t('postingPlan.noAiLine'),
      ],
    }] : []),
    ...regularPlans.map((plan) => (compareTrialLine(plan.id)
      ? { ...plan, features: [compareTrialLine(plan.id), ...plan.features] }
      : plan)),
    ...extraPlans.map((plan) => (compareTrialLine(plan.id)
      ? { ...plan, features: [compareTrialLine(plan.id), ...plan.features] }
      : plan)),
  ];
  // Selection lookup always considers the full set (including a collapsed
  // Agency) so a stale selectedPlanKey never falls through to plans[0].
  const allSelectablePlans = [...(postingPlanEntry ? [postingPlanEntry] : []), ...regularPlans, ...extraPlans];
  const selected = allSelectablePlans.find((plan) => plan.id === selectedPlanKey) || allSelectablePlans[0];
  const postingSelected = selected?.id === 'posting';
  const accountId = user?.id || null;
  // Publish authorization only after React commits this account/selection.
  // A render that bails out must not invalidate the visible button's handler.
  const pickerIdentity = useMemo(() => ({ accountId, targetKey: selectedPlanKey }), [accountId, selectedPlanKey]);
  useLayoutEffect(() => {
    const committed = { ...pickerIdentity, identity: pickerIdentity, mounted: true };
    renderContextRef.current = committed;
    return () => { committed.mounted = false; };
  }, [pickerIdentity]);

  const reviewTarget = (planKey) => {
    if (planKey === 'posting') return postingPlanEntry && publishTrack ? {
      planKey,
      name: postingPlanEntry.name,
      priceCents: publishTrack.display?.priceCents,
      priceFormatted: publishTrack.line.unitFormatted,
    } : null;
    const plan = [...regularPlans, ...extraPlans].find((candidate) => candidate.id === planKey);
    return plan ? {
      planKey,
      name: plan.name,
      priceCents: plan.priceCents,
      priceFormatted: plan.price,
    } : null;
  };

  const makeSwitchReview = (ent, targetKey, accounts = null, reviewAccountId = accountId) => {
    const data = ent?.data || ent || {};
    const target = reviewTarget(targetKey);
    const scheduled = resolveScheduledDowngrade(data);
    if (!target || !isRecognizedBillingState(data)) return null;
    return buildPlanSwitchReview({
      accountId: reviewAccountId,
      billingStatus: data.billingStatus || data.subscriptionStatus || null,
      accessState: data.accessState,
      currentPlanId: data.plan || data.planKey || null,
      cancelAtPeriodEnd: data.cancelAtPeriodEnd,
      scheduledPlan: scheduled?.plan ?? null,
      scheduledPlanEffectiveAt: scheduled?.effectiveAt ?? null,
      targetPlanKey: target.planKey,
      targetName: target.name,
      targetPriceCents: target.priceCents,
      targetPriceFormatted: target.priceFormatted,
      postingDisplay: publishTrack?.display,
      connectedSocials: targetKey === 'posting' ? accounts : null,
    });
  };

  // TASK-313: the existing ChatGPT-origin rule (below, gated on
  // postingPlanActive exactly like it always was) takes precedence over the
  // URL intent. appliedIntentPlanKey is non-null only when the intent
  // actually won the decision — used to gate the one-time funnel event and
  // the scroll-into-view below, so neither fires for the pre-existing
  // chatGptOrigin default or the plain 'growth' default.
  const preselectedTarget = useMemo(() => resolvePreselectedPlan({
    intentPlanKey: planIntentKey,
    chatGptOrigin,
    postingPlanActive,
    fallback: 'growth',
  }), [planIntentKey, chatGptOrigin, postingPlanActive]);
  const appliedIntentPlanKey = (planIntentKey && preselectedTarget === planIntentKey) ? planIntentKey : null;

  const trialEndDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + TRIAL_DAYS);
    return new Intl.DateTimeFormat(i18n.resolvedLanguage || i18n.language || 'en', {
      day: 'numeric', month: 'long', year: 'numeric',
    }).format(date);
  }, [i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    let cancelled = false;
    const expectedAccount = accountId;
    api.billing.getEntitlements()
      .then((ent) => {
        if (!cancelled && expectedAccount === accountId) setBilling({ state: 'ready', ...getBillingState(ent) });
      })
      .catch(() => {
        if (!cancelled && expectedAccount === accountId) setBilling({ state: 'error', billingStatus: null, hasLiveSubscription: false });
      });
    return () => { cancelled = true; };
  }, [accountId]);

  useEffect(() => {
    if (!postingPlanActive) return;
    let cancelled = false;
    api.socialAccounts.list()
      .then((response) => { if (!cancelled) setConnectedSocials(response?.accounts || response?.data?.accounts || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [postingPlanActive]);

  // TASK-313: apply the ChatGPT-origin default / URL plan intent decision.
  // Subsumes the old bare `if (chatGptOrigin) setSelectedPlanKey('posting')`
  // — resolvePreselectedPlan's first branch is exactly that rule.
  useEffect(() => {
    setSelectedPlanKey(preselectedTarget);
  }, [preselectedTarget]);

  // Agency sits behind the collapsed "See all plans" toggle — expand it so
  // an incoming ?plan=agency intent is actually visible to scroll to.
  useEffect(() => {
    if (appliedIntentPlanKey === 'agency') setShowAllPlans(true);
  }, [appliedIntentPlanKey]);

  useEffect(() => {
    if (!appliedIntentPlanKey || appliedIntentTrackedRef.current) return;
    appliedIntentTrackedRef.current = true;
    try { track('plan_intent_applied', { plan: appliedIntentPlanKey }); } catch { /* fail-silent, matches funnel.js contract */ }
  }, [appliedIntentPlanKey]);

  useEffect(() => {
    if (!appliedIntentPlanKey || typeof window === 'undefined') return undefined;
    const raf = window.requestAnimationFrame(() => {
      document.querySelector(`[data-plan="${appliedIntentPlanKey}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [appliedIntentPlanKey, showAllPlans]);

  useEffect(() => {
    reviewRequestRef.current += 1;
    reviewLifecycleRef.current += 1;
    activeReviewRef.current = null;
    operationRef.current = null;
    busyRef.current = false;
    setSwitchReview(null);
    setSwitchReviewLoading(false);
    setSwitchReviewError(false);
    setSwitchReviewChanged(false);
    setMode('select');
    return () => {
      reviewRequestRef.current += 1;
      reviewLifecycleRef.current += 1;
      activeReviewRef.current = null;
      operationRef.current = null;
    };
  }, [accountId, selectedPlanKey]);

  const readSwitchReview = async (authorization) => {
    if (!authorization || activeReviewRef.current !== authorization || operationRef.current) return false;
    const current = renderContextRef.current;
    if (!current?.mounted || current.accountId !== authorization.accountId || current.targetKey !== authorization.targetKey) return false;
    const owner = {};
    operationRef.current = owner;
    busyRef.current = true;
    const request = ++reviewRequestRef.current;
    const lifecycle = reviewLifecycleRef.current;
    const expectedAccount = authorization.accountId;
    const targetKey = authorization.targetKey;
    let retainOwner = false;
    const isCurrent = () => request === reviewRequestRef.current
      && lifecycle === reviewLifecycleRef.current
      && operationRef.current === owner
      && renderContextRef.current?.mounted
      && expectedAccount === renderContextRef.current.accountId
      && targetKey === renderContextRef.current.targetKey
      && activeReviewRef.current === authorization;
    setSwitchReviewLoading(true);
    setSwitchReviewError(false);
    setSwitchReviewChanged(false);
    try {
      api.clearCache();
      const ent = await api.billing.getEntitlements({ fresh: true });
      if (!isCurrent()) return false;
      const data = ent?.data || ent || {};
      if (!isRecognizedBillingState(data)) throw new Error('Invalid billing response');
      const latest = getBillingState(ent);
      setBilling({ state: 'ready', ...latest });
      if (!latest.hasLiveSubscription) throw new Error('Subscription is no longer changeable');
      if (latest.currentPlanId === targetKey) {
        retainOwner = true;
        return { owner, authorization, isCurrent, samePlan: true };
      }
      const socialResponse = targetKey === 'posting' ? await api.socialAccounts.list() : null;
      if (!isCurrent()) return false;
      if (targetKey === 'posting' && !Array.isArray(socialResponse?.accounts)) throw new Error('Invalid accounts response');
      const nextReview = makeSwitchReview(ent, targetKey, socialResponse?.accounts ?? null, expectedAccount);
      if (!nextReview) throw new Error('Billing state is not eligible for this change');
      const nextAuthorization = {
        generation: ++reviewGenerationRef.current,
        accountId: expectedAccount,
        targetKey,
        review: nextReview,
      };
      setSwitchReview(nextReview);
      activeReviewRef.current = nextAuthorization;
      if (!planSwitchReviewMatches(authorization.review, nextReview)) {
        setSwitchReviewChanged(true);
        return false;
      }
      retainOwner = true;
      return {
        owner,
        authorization: nextAuthorization,
        isCurrent: () => operationRef.current === owner
          && lifecycle === reviewLifecycleRef.current
          && activeReviewRef.current === nextAuthorization
          && renderContextRef.current?.mounted
          && expectedAccount === renderContextRef.current.accountId
          && targetKey === renderContextRef.current.targetKey,
      };
    } catch (_) {
      if (isCurrent()) setSwitchReviewError(true);
      return false;
    } finally {
      if (!retainOwner && operationRef.current === owner) {
        operationRef.current = null;
        busyRef.current = false;
        if (lifecycle === reviewLifecycleRef.current) setSwitchReviewLoading(false);
      }
    }
  };

  const completeReviewedSwitch = async (review, transaction) => {
    const { owner, authorization } = transaction;
    const isCurrent = transaction.isCurrent;
    const finish = () => {
      if (operationRef.current !== owner) return;
      const ownedCurrentLifecycle = isCurrent();
      operationRef.current = null;
      busyRef.current = false;
      if (activeReviewRef.current === authorization) activeReviewRef.current = null;
      if (ownedCurrentLifecycle) setSwitchReviewLoading(false);
    };
    try {
      if (!isCurrent()) return;
      const result = await api.billing.changePlan({ newPlanKey: review.planKey });
      if (!isCurrent()) return;
      const resultData = result?.data ?? result ?? {};
      if (resultData.requiresAction) {
        if (resultData.hostedInvoiceUrl) {
          if (!isCurrent()) return;
          window.location.assign(resultData.hostedInvoiceUrl);
          return;
        }
        finish();
        setMode('select');
        setError(t('plansPage.switchRequiresAction', { plan: review.targetName }));
        setErrorMeta({ terminal: true, showBillingLink: false });
        return;
      }
      if (!isCurrent()) return;
      clearPlanIntent();
      await onComplete?.();
    } catch (err) {
      if (!isCurrent()) return;
      finish();
      setMode('select');
      const resolvedError = resolveSwitchError({
        code: err?.code,
        reason: err?.details?.reason,
        status: err?.details?.status,
        t,
      });
      setError(resolvedError ? resolvedError.message : t('checkout.checkoutError'));
      setErrorMeta(resolvedError ? {
        terminal: true,
        showBillingLink: resolvedError.cta === 'billingSettings',
      } : null);
    } finally {
      if (isCurrent()) finish();
    }
  };

  const confirmSwitchReview = async () => {
    const authorization = activeReviewRef.current;
    if (!switchReview || !authorization || authorization.review !== switchReview || switchReviewLoading) return;
    const reviewed = switchReview;
    const transaction = await readSwitchReview(authorization);
    if (!transaction || !transaction.isCurrent()) return;
    setSwitchReview(null);
    setMode('handoff');
    if (transaction.samePlan) {
      if (!transaction.isCurrent()) return;
      clearPlanIntent();
      await onComplete?.();
      if (operationRef.current === transaction.owner) {
        operationRef.current = null;
        busyRef.current = false;
        if (activeReviewRef.current === authorization) activeReviewRef.current = null;
        setSwitchReviewLoading(false);
      }
      return;
    }
    await completeReviewedSwitch(reviewed, transaction);
  };

  const closeSwitchReview = () => {
    if (switchReviewLoading || operationRef.current) return;
    if (!switchReview || activeReviewRef.current?.review !== switchReview) return;
    reviewRequestRef.current += 1;
    activeReviewRef.current = null;
    setSwitchReview(null);
    setSwitchReviewError(false);
    setSwitchReviewChanged(false);
  };

  async function handleContinue() {
    if (operationRef.current) return;
    if (!renderContextRef.current?.mounted || renderContextRef.current.identity !== pickerIdentity) return;
    const owner = {};
    operationRef.current = owner;
    busyRef.current = true;
    const requestAccount = accountId;
    const requestTarget = selectedPlanKey;
    const lifecycle = reviewLifecycleRef.current;
    const isCurrent = () => lifecycle === reviewLifecycleRef.current
      && operationRef.current === owner
      && renderContextRef.current?.mounted
      && requestAccount === renderContextRef.current.accountId
      && requestTarget === renderContextRef.current.targetKey;
    setMode('handoff');
    setError(null);
    setErrorMeta(null);

    try {
      // Re-check immediately before the money call. create-checkout must never
      // receive an org that acquired a live subscription after initial render.
      api.clearCache();
      const ent = await api.billing.getEntitlements({ fresh: true });
      if (!isCurrent()) return;
      const data = ent?.data || ent || {};
      if (!isRecognizedBillingState(data)) throw new Error('Invalid billing response');
      const latest = getBillingState(ent);
      setBilling({ state: 'ready', ...latest });
      if (latest.hasLiveSubscription) {
        if (latest.currentPlanId === requestTarget) {
          if (!isCurrent()) return;
          clearPlanIntent();
          await onComplete?.();
          if (operationRef.current === owner) {
            operationRef.current = null;
            busyRef.current = false;
          }
          return;
        }
        const accounts = requestTarget === 'posting'
          ? await api.socialAccounts.list()
          : null;
        if (!isCurrent()) return;
        if (requestTarget === 'posting' && !Array.isArray(accounts?.accounts)) throw new Error('Invalid accounts response');
        const nextReview = makeSwitchReview(ent, requestTarget, accounts?.accounts ?? null, requestAccount);
        if (!nextReview) throw new Error('Billing review unavailable');
        const authorization = {
          generation: ++reviewGenerationRef.current,
          accountId: requestAccount,
          targetKey: requestTarget,
          review: nextReview,
        };
        activeReviewRef.current = authorization;
        if (operationRef.current !== owner) return;
        operationRef.current = null;
        busyRef.current = false;
        setMode('select');
        setSwitchReview(nextReview);
        return;
      }

      if (latest.accessState !== 'new') throw new Error('Subscription is not eligible for checkout');

      const returnTo = returnToOverride || `${window.location.pathname}${window.location.search}`;
      try { onBeforeCheckout?.(requestTarget); } catch { /* bookkeeping never blocks checkout */ }
      if (requestTarget === 'posting') {
        await startCheckout('posting', { returnTo });
        if (!isCurrent()) return;
        clearPlanIntent(); // TASK-313: a successful checkout start consumes the intent.
        return;
      }
      // TASK-331 (A15, phase-1-fixes round 3): a returning customer
      // (hasSubscriptionHistory) must never be re-granted a fresh trial —
      // startCheckout only sets subscription_data[trial_period_days] when
      // trialSignup is truthy, so passing false here takes
      // create-checkout.ts's existing no-trial path (the same one Plans.jsx's
      // checkout buttons already use for an existing customer). This screen
      // only renders for accessState 'new', and the backend's
      // hasSubscriptionHistory fix (an abandoned Stripe `incomplete` checkout
      // no longer counts as history — entitlement-repository.ts's
      // hasSuccessfulInvoice()) means 'new' + hasSubscriptionHistory=true
      // SHOULD no longer occur. "Should not" is not "cannot": this line, and
      // the offerTrial-gated copy below, both stay keyed on
      // hasSubscriptionHistory itself rather than assuming the 'new' state
      // guarantees it false.
      await startCheckout(requestTarget, { trialSignup: !latest.hasSubscriptionHistory, returnTo });
      if (!isCurrent()) return;
      clearPlanIntent(); // TASK-313: a successful checkout start consumes the intent.
    } catch (err) {
      if (!isCurrent()) return;
      if (operationRef.current !== owner) return;
      operationRef.current = null;
      busyRef.current = false;
      setMode('select');
      // Preserve the existing machine-readable billing error copy. Generic
      // checkout/review-read failures keep the pre-existing fallback.
      const resolvedError = resolveSwitchError({
        code: err?.code,
        reason: err?.details?.reason,
        status: err?.details?.status,
        t,
      });
      setError(resolvedError ? resolvedError.message : t('checkout.checkoutError'));
      // MINOR 2 (round 6): a resolved error already states the actual next
      // step (add a card / pick a plan / pay the open invoice / try again in
      // a moment) — appending the generic "Please try again" turned it into a
      // run-on with contradictory or redundant advice for EVERY resolved
      // reason, PAYMENT_METHOD_UNVERIFIED included (its own copy already ends
      // with "try again in a moment"). So terminal is always true for a
      // resolved error now — the suffix is reserved for the unresolved/
      // generic path (errorMeta null) below. showBillingLink reuses the same
      // 'billingSettings' cta resolveSwitchError already resolves
      // (PAYMENT_METHOD_REQUIRED / SUBSCRIPTION_NOT_CHANGEABLE) — same route
      // Plans.jsx's switchError banner uses, via paymentIssueLinks().
      setErrorMeta(resolvedError ? {
        terminal: true,
        showBillingLink: resolvedError.cta === 'billingSettings',
      } : null);
    } finally {
      if (operationRef.current === owner && !isCurrent()) operationRef.current = null;
    }
  }

  // TASK-313: routes every explicit card/band click through here so picking
  // a DIFFERENT plan than the one the intent preselected drops that intent —
  // it must not keep re-forcing itself on a later visit to this same picker.
  function handleSelectPlan(planKey) {
    if (planKey !== selectedPlanKey) clearPlanIntent();
    setSelectedPlanKey(planKey);
  }

  async function handleSignOut() {
    await logout?.();
    window.location.assign('/login');
  }

  if (mode === 'handoff') {
    return (
      <div className="tcc-page ck-page ck-handoff-page">
        <div className="hand" role="status" aria-live="polite">
          <div className="marks">
            <img src="/assets/dathent-logo.png" alt="Dathent" />
            <span className="dots" aria-hidden="true"><i /><i /><i /></span>
            <span className="stripe-tile" aria-label="Stripe">S</span>
          </div>
          <div className="spin" aria-hidden="true" />
          <h1>{t('checkout.handoffTitle')}</h1>
          <p className="sub">{t('checkout.handoffSubtitle')}</p>
          <span className="lock"><LockIcon />{t('checkout.handoffTrust')}</span>
        </div>
      </div>
    );
  }

  const cancelledCheckout = new URLSearchParams(window.location.search).get('checkout') === 'cancelled';
  // fix (2026-09-07): offerTrial above (now hoisted near compareList, see
  // its own comment) is ORG eligibility only (never had a
  // subscription) — it says nothing about whether the currently
  // selected/highlighted PLAN itself has a trial. Posting (planHasTrial ===
  // false) never grants a trial regardless of hasSubscriptionHistory (see
  // startCheckout('posting') below and planTrial.js's header), so the
  // header subtitle must also gate on the selected plan, not just org
  // history — otherwise a first-time visitor with Posting highlighted saw
  // "Pick a plan to start your 7-day trial" even though picking Posting
  // charges them immediately.
  const showTrialSubtitle = offerTrial && planHasTrial(selectedPlanKey);
  // TASK-298: adaptive CTA copy — Scheduler has no trial, so its continue
  // label states the recurring per-account price up front instead of the
  // generic "Continue to secure checkout".
  const baseCtaLabel = postingSelected
    ? t('postingGate.continueScheduler', { price: publishTrack?.line?.unitFormatted })
    : t('checkout.continue');
  const ctaLabel = billing.state === 'error' ? t('checkout.retry') : baseCtaLabel;
  // TASK-298 fix round (BLOCKER 2): the charge-disclosure copy is computed
  // ONCE and rendered in BOTH places it now needs to appear — under the
  // desktop CTA (.ck-cta-row, hidden ≤540px) and inside the mobile sticky
  // dock (.ck-dock-in) — so a phone user always sees it before Stripe.
  const termsCopy = postingSelected
    ? t('checkout.postingTerms')
    : offerTrial
      ? t('checkout.trialTerms', { price: selected.price, date: trialEndDate })
      // TASK-331 phase-1-fixes round 3 (fix #4): non-trial variant — never
      // mentions a trial for a returning customer (see offerTrial above).
      : t('checkout.chargeTermsNoTrial', { price: selected.price });
  // GH-912 (Stripe Managed Payments): a NEW subscription started here is sold
  // through Link — say so under the terms in both places termsCopy renders,
  // before the hand-off to Stripe. The net-price terms above are unchanged.
  const managedPaymentsNote = resolveManagedPaymentsNewSubscriptions({ catalogFlag: catalogManagedPayments, entitlements: billing })
    && !billing.hasLiveSubscription
    ? t('checkout.managedPaymentsNote')
    : null;

  const cta = (mobile = false) => (
    <button
      type="button"
      className="ck-cta"
      onClick={handleContinue}
      disabled={billing.state === 'loading'}
    >
      <span>{billing.state === 'loading' ? t('checkout.checking') : ctaLabel}</span>
      <span className="ck-sr">{t('checkout.opensStripe')}</span>
      {!mobile && <ArrowIcon />}
    </button>
  );

  return (
    <div className={`tcc-page ck-page${managedPaymentsNote ? ' has-mp-note' : ''}`}>
      {onBack && <button type="button" className="fpw-back" onClick={onBack}>← {t('freePreview.wallBack')}</button>}
      <button type="button" className="tcc-signout" onClick={handleSignOut}>
        {t('trial.notYouSignOut', { defaultValue: 'Not you? Log out' })}
      </button>
      <div className="tcc-wrap ck-wrap">
        <header className="ck-head">
          <div className="ck-brand"><img src="/assets/dathent-logo.png" alt="Dathent" /><span className="wm">Dathent</span></div>
          {previewContext && <div className="fpw-context" role="note">{(previewContext.saved ? ['freePreview.wallSaved'] : wallContextKeys(previewContext.source, { aiPlan: planHasTrial(selectedPlanKey) })).map(key => <span key={key}>{t(key, { feature: previewContext.featureLabel || '' })} </span>)}</div>}
          {/* TASK-298 B1 fix: the header is STATIC — it no longer switches on
              the selected plan. postingPlan.noAiNote is never referenced here. */}
          <h1 className="ck-h">{t('postingGate.title')}</h1>
          <p className="ck-sub">{showTrialSubtitle ? t('checkout.pickSubtitle', { days: TRIAL_DAYS }) : t('checkout.pickSubtitleNoTrial')}</p>
          <ProductVideoLink />
          {cancelledCheckout && <div className="ck-notice" role="status">{t('checkout.cancelled')}</div>}
          {billing.billingStatus === 'trialing' && <div className="ck-notice" role="note">{t('checkout.trialAlreadyRunning')}</div>}
          {billing.hasLiveSubscription && billing.billingStatus !== 'trialing' && <div className="ck-notice" role="note">{t('checkout.subscriptionAlreadyRunning')}</div>}
          {/* MINOR 2/3 (round 6): every RESOLVED switch reason (PAYMENT_METHOD_
              REQUIRED / PAYMENT_METHOD_UNVERIFIED / SUBSCRIPTION_ENDED /
              SUBSCRIPTION_NOT_CHANGEABLE) and the requiresAction-without-a-
              hosted-invoice-URL fallback already state the actual next step
              — "Please try again" made it read as a run-on with contradictory
              or redundant advice (PAYMENT_METHOD_UNVERIFIED's own copy already
              ends with "try again in a moment"). errorMeta.terminal is always
              true whenever errorMeta is set (see setErrorMeta above), so the
              suffix only ever shows for an unresolved/generic error
              (errorMeta null). */}
          {error && (
            <div className="ck-error" role="alert">
              <span>{error}</span>
              {!errorMeta?.terminal && <> {t('checkout.tryAgain')}</>}
              {errorMeta?.showBillingLink && (
                <>
                  {' '}
                  <button
                    type="button"
                    // BLOCKER (round 6): a hard navigation, not SPA navigate(). This
                    // screen renders ONLY because RequireActiveBilling's gate decision
                    // (App.jsx) resolved 'gate' — that decision is computed once in a
                    // mount-time effect and never recomputed on client-side route
                    // changes, so navigate() would just change the URL underneath this
                    // same stuck screen. A full page load re-runs the gate against
                    // fresh entitlements, which is exactly what should happen once the
                    // org has a live subscription again.
                    onClick={() => { window.location.assign(paymentIssueLinks(null, null).billingSettings); }}
                    style={{ background: 'none', border: 0, padding: 0, color: 'inherit', cursor: 'pointer', textDecoration: 'underline', font: 'inherit' }}
                  >
                    {t('paymentIssue.billingSettings')}
                  </button>
                </>
              )}
            </div>
          )}
        </header>

        <div
          id="ck-plan-grid"
          className={`ck-grid ck-stack ${gridColsClass(aiCards.length)}`}
          role="radiogroup"
          aria-label={t('checkout.choosePlan')}
        >
          {/* Track 1 — Scheduler ("Publish only"): a full-width band, not a
              card. Judge's fix-round decision: mobile order is always band ->
              divider -> Growth -> Starter -> Pro -> Agency on BOTH surfaces,
              so `.publish-first` is gone — chatGptOrigin still preselects the
              plan (see the effect above), it just no longer reorders. */}
          {publishTrack && (
            <button
              type="button"
              className={`ck-band ${postingSelected ? 'sel' : ''}`}
              role="radio"
              aria-checked={postingSelected}
              data-plan="posting"
              onClick={() => handleSelectPlan('posting')}
            >
              <span className="ck-ring"><i /></span>
              <span className="ck-band-left">
                <span className="ck-band-toprow">
                  <span className="ck-band-chip">{t('postingPlan.trackLabel')}</span>
                  <span className="nm">{t('postingPlan.name')}</span>
                  {/* Founder request (2026-09-09): make "no trial here, 7-day
                      trial on the AI plans" obvious at a glance. Only shown
                      when the AI cards below actually show a trial chip
                      (offerTrial) — otherwise this org is a returning
                      customer with no trial ANYWHERE, and promising nothing
                      would be misleading contrast, not clarity. */}
                  {offerTrial && <span className="ck-trial-chip is-none">{t('checkout.noTrialChip')}</span>}
                </span>
                <span className="ck-desc">{t('postingPlan.publishTrackSubtitle')}</span>
              </span>
              <span className="ck-band-mid">
                <ul className="ck-feats">
                  {[1, 2, 3].map((index) => <li key={index}><CheckIcon /><span>{t(`postingPlan.included${index}`)}</span></li>)}
                </ul>
                <span className="ck-band-excl" aria-label={t('postingPlan.excludedTitle')}>
                  {[1, 2, 3, 4].map((index) => (
                    <span key={index} className="ck-band-excl-item">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="m5 19 14-14" /></svg>
                      {t(`postingPlan.notIncluded${index}`)}
                    </span>
                  ))}
                </span>
              </span>
              <span className="ck-band-price">
                <span className="amt">{publishTrack.line.unitFormatted}</span>
                <span className="u">{t('postingPlan.unit')}</span>
                <span className="ck-band-total">{publishTrack.liveTotalCopy}</span>
                {/* TASK-331: glanceable BEFORE selection — this screen only
                    ever renders for accessState 'new' (see getBillingState
                    above), so there is no "already on Scheduler" state to
                    guard against here (unlike Plans.jsx's currentPlanId
                    check). Same key/style as Plans.jsx's price-column note
                    (.plans-band-total), reused verbatim — no new locale key. */}
                <span className="ck-band-total">{t('postingPlan.noTrialNote')}</span>
              </span>
            </button>
          )}

          {/* Track 2 — AI content plans divider. Only rendered alongside the
              band: with no Track 1 to contrast against, the header is
              redundant (design brief States: "Catalog loading / fetch failed"). */}
          {publishTrack && (
            <div className="ck-track-head" data-track-head>
              <span className="ck-track-title">{t('postingPlan.aiTrackTitle')}</span>
              <span className="ck-track-sub">{t('postingPlan.aiTrackSubtitle')}</span>
            </div>
          )}

          {aiCards.map((plan) => {
            const selectedPlan = plan.id === selectedPlanKey;
            return (
              <button
                type="button"
                key={plan.id}
                className={`ck-card ${selectedPlan ? 'sel' : ''}`}
                role="radio"
                aria-checked={selectedPlan}
                data-plan={plan.id}
                onClick={() => handleSelectPlan(plan.id)}
              >
                {plan.featured && <span className="ck-pill ck-rec-desktop">{t('checkout.recommended')}</span>}
                <span className="top">
                  <span className="ck-ring"><i /></span>
                  <span className="nm">{plan.name}{plan.featured && <span className="ck-rec-inline">{t('checkout.recommended')}</span>}</span>
                  <span className="ck-price pr" data-slot={`price:${plan.id}`}>{plan.price}<span className="u">{plan.unit}</span></span>
                </span>
                {/* Founder request (2026-09-09): trial status at a glance,
                    same chip family as the Scheduler band's "No free trial"
                    (planHasTrial is always true here in practice — every
                    aiCards entry is an AI plan, never 'posting' — kept
                    explicit so a future no-trial AI plan is covered for
                    free). */}
                {offerTrial && planHasTrial(plan.id) && (
                  <span className="ck-trial-chip">{t('checkout.freeTrial', { days: TRIAL_DAYS })}</span>
                )}
                <span className="ck-desc">{plan.description}</span>
                {/* PlanSelect a11y fix (round 2, "Other minors"): plain text
                    only here — this .ck-card IS a role="radio" button, so
                    nesting the chip's role="button" inside it would be
                    interactive-in-interactive content. The genuinely
                    separate control below (outside every card) opens the
                    sheet instead, for the currently-selected plan. */}
                <SocialAddonProvenanceLine pricing={socialAddonPricingFor(plan.id, plan.name)} interactive={false} />
                <ul className="ck-feats">
                  {plan.features.slice(0, 4).map((feature) => <li key={feature}><CheckIcon /><span>{feature}</span></li>)}
                </ul>
              </button>
            );
          })}
        </div>

        {/* PlanSelect a11y fix (round 2): the ONE real sibling control for
            the selected plan's "N connected" segment — see the comment
            above. Only rendered once there's actually overpricing to manage
            (shouldOfferAccountsSheet, the one shared gate). */}
        {(() => {
          const selectedPricing = socialAddonPricingFor(selected?.id, selected?.name);
          if (!shouldOfferAccountsSheet(selectedPricing)) return null;
          return (
            <button
              type="button"
              className="ck-social-addon-link"
              aria-haspopup="dialog"
              aria-expanded={addonSheet.open}
              onClick={(e) => {
                addonSheetAnchorRef.current = e.currentTarget;
                setAddonSheet({ open: true, planId: selected.id, planName: selected.name });
              }}
            >
              {t('socialAddon.viewConnectedAccounts', { count: selectedPricing.connected })}
            </button>
          );
        })()}

        <button
          type="button"
          className="ck-more-toggle"
          aria-expanded={showAllPlans}
          aria-controls="ck-plan-grid"
          onClick={() => setShowAllPlans((v) => !v)}
        >
          {showAllPlans ? t('plansPage.showFewerPlans') : t('plansPage.seeAllPlans')}
        </button>

        <details className="ck-cmp">
          <summary>{t('checkout.compare')} <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg></summary>
          <div className="ck-cmp-grid">
            {compareList.map((plan) => (
              <div className="ck-cmp-col" key={plan.id}>
                <span className="cn">{plan.name}</span>
                <ul>{plan.features.map((feature) => <li key={feature}><CheckIcon /><span>{feature}</span></li>)}</ul>
              </div>
            ))}
          </div>
        </details>

        <div className="ck-cta-row">
          {cta()}
          <p className="ck-terms">{termsCopy}</p>
          {managedPaymentsNote && <p className="ck-terms ck-mp-note">{managedPaymentsNote}</p>}
          <span className="ck-stripe"><LockIcon />{t('checkout.secureByStripe')}</span>
        </div>

        <p className="ck-fine">
          {t('checkout.finePrint')}{' '}
          <a href="https://dathent.ai/terms" target="_blank" rel="noopener noreferrer">{t('checkout.terms')}</a>
          {' · '}
          <a href="https://dathent.ai/privacy" target="_blank" rel="noopener noreferrer">{t('checkout.privacy')}</a>
        </p>
      </div>

      <div className="ck-dock">
        <div className="ck-dock-in">
          <div className="ck-dock-sum">
            <span>
              {t('checkout.selected')}: <b>
                {/* TASK-298 fix round (MINOR 9): Scheduler's unit has no
                    leading slash ("per connected social / month"), so
                    concatenating price+unit read as "€3.99per connected...".
                    Scheduler shows only the live total; AI plans keep the
                    price+unit form (unit already starts with "/"). */}
                {postingSelected
                  ? <>{selected.name} · {publishTrack?.liveTotalCopy || ''}</>
                  : <>{selected.name} · {selected.price}{selected.unit}{offerTrial && planHasTrial(selectedPlanKey) ? ` ${t('checkout.afterTrial')}` : ''}</>}
              </b>
            </span>
            {!postingSelected && offerTrial && planHasTrial(selectedPlanKey) && <span className="zero">{t('checkout.dueToday')}</span>}
          </div>
          {/* BLOCKER 2: same disclosure as .ck-terms above, duplicated here
              since .ck-cta-row is display:none at this width. */}
          <p className="ck-terms ck-terms-dock">{termsCopy}</p>
          {managedPaymentsNote && <p className="ck-terms ck-mp-note-dock">{managedPaymentsNote}</p>}
          {cta(true)}
          <div className="ck-stripe"><LockIcon />{t('checkout.secureByStripe')}</div>
        </div>
      </div>

      {/* TASK-329: no "Connect another" entry point here — this screen only
          ever renders pre-subscription (accessState 'new'); Settings is
          reachable only once RequireActiveBilling's gate clears, so sending
          the user there could bounce them straight back. */}
      <ConnectedAccountsSheet
        open={addonSheet.open}
        pricing={addonSheet.open ? socialAddonPricingFor(addonSheet.planId, addonSheet.planName) : null}
        anchorRef={addonSheetAnchorRef}
        onClose={() => setAddonSheet((s) => ({ ...s, open: false }))}
        onDisconnected={() => {
          api.billing.getEntitlements()
            .then((ent) => setBilling({ state: 'ready', ...getBillingState(ent) }))
            .catch(() => {});
        }}
      />
      <PlanSwitchReviewDialog
        review={switchReview}
        changed={switchReviewChanged}
        error={switchReviewError}
        confirming={switchReviewLoading}
        confirmDisabled={!switchReview || switchReview.accountId !== accountId || switchReview.planKey !== selectedPlanKey}
        onConfirm={confirmSwitchReview}
        onCancel={closeSwitchReview}
      />
    </div>
  );
}
